import {useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DataTable, type Column, type ColumnGroup} from '@/components/data-table'
import {HelpIcon} from '@/components/help-icon'
import {Button} from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {KebabGlyph} from '@/components/ui/icons'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Switch} from '@/components/ui/switch'
import {Tag} from '@/components/ui/tag'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {FiltrosDeEstado, type OpcionEstado} from '@/components/ui/filtros-de-estado'
import {PedirSoporteDialog} from '@/components/pedir-soporte-dialog'
import {useOperatorPage} from '@/components/no-access-state'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useIsMobile} from '@/hooks/common/use-media-query'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  usePedirEnBloque,
  useRejilla,
  useRejillaColumnas,
  type ColumnaRejilla,
  type CruceRejilla,
  type FilaRejilla,
} from '@/hooks/empresa/cumplimiento/use-cumplimiento'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {SearchInput} from '@/components/ui/search-input'

const TONOS = {verde: 'green', amarillo: 'amber', rojo: 'red', en_curso: 'enCurso', gris: 'neutral'} as const

type Color = keyof typeof TONOS

/** Lo peor primero; `sin_veredicto` es como el servidor entiende «sin color», que un query param no dice con nulo. */
const VEREDICTOS = ['rojo', 'amarillo', 'verde', 'sin_veredicto'] as const

type VeredictoFiltro = (typeof VEREDICTOS)[number] | 'todos'

/** Rótulo por la clave de la casilla 0312 (o el nombre autorado plegado), nunca recortando el nombre:
 * un recorte no sabe qué palabra distingue. Mismos rótulos que `TIPO_POR_SUFIJO` en examenes-matriz. */
const ROTULO_POR_SUFIJO: Record<string, string> = {
  examen_ingreso: 'ingreso',
  examen_periodico: 'periodico',
  examen_egreso: 'egreso',
  examen_post_incapacidad: 'postIncapacidad',
  examen_cambio_ocupacion: 'cambioOcupacion',
  examen_reintegro: 'reintegro',
  conceptos_aptitud: 'aptitud',
  certificado_afiliacion: 'afiliacion',
  planilla_pila: 'pila',
  induccion_reinduccion: 'induccion',
  certificado_curso_50h: 'curso50h',
}

const ROTULO_POR_NOMBRE: Record<string, string> = {
  'curso de trabajo seguro en alturas': 'trabajoSeguro',
  'reentrenamiento de trabajo seguro en alturas': 'reentrenamiento',
  'curso de espacios confinados': 'espaciosConfinados',
  'competencia para trabajos electricos': 'electricos',
  'licencia de conduccion vigente': 'conduccion',
  'manipulacion de alimentos': 'alimentos',
  'permiso para porte de armas': 'armas',
  'manipulacion manual de cargas': 'cargas',
  'uso seguro de herramientas y equipos': 'herramientas',
  'manejo seguro de sustancias quimicas (sga)': 'quimicas',
  'proteccion respiratoria': 'respiratoria',
  'conservacion auditiva': 'auditiva',
  'licencia en seguridad y salud en el trabajo': 'licenciaSst',
  'trabajo en caliente': 'caliente',
  'izaje de cargas': 'izaje',
  excavaciones: 'excavaciones',
  'riesgo biologico': 'biologico',
  'exposicion a radiaciones': 'radiaciones',
  'exposicion a vibraciones': 'vibraciones',
}

function plegar(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

const CONECTORES = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'en', 'y', 'a', 'para', 'por', 'con', 'al', 'su'])

/** La clave de copy del rótulo corto, o `null` si manda el respaldo. */
export function claveDeRotulo(columna: {nombre: string; soporte_key?: string | null}): string | null {
  const sufijo = columna.soporte_key?.split('/')[1]
  return (sufijo && ROTULO_POR_SUFIJO[sufijo]) ?? ROTULO_POR_NOMBRE[plegar(columna.nombre)] ?? null
}

/** Respaldo: las dos primeras palabras que distinguen, enteras; el nombre completo va en el `title`. */
export function nombreCortoDeRequisito(nombre: string): string {
  const palabras = nombre.split(/\s+/).filter(Boolean)
  const fuertes = palabras.filter((palabra) => !CONECTORES.has(plegar(palabra)))
  return (fuertes.length ? fuertes : palabras).slice(0, 2).join(' ')
}

/** ~0,52 rem por carácter en la versalita mono, con piso de 7 rem (`w-28`). Viaja como longitud CSS:
 * Tailwind no compila una clase armada en tiempo de ejecución. */
export function anchoDeColumna(rotulos: string[]): number {
  const masLarga = rotulos
    .flatMap((rotulo) => rotulo.split(/\s+/))
    .reduce((max, palabra) => Math.max(max, palabra.length), 0)
  return Math.max(7, Math.round((masLarga * 0.52 + 1.6) * 10) / 10)
}

/** Estado corto y no la frase de la ficha: esa frase interpola fechas que un cruce no trae. */
function CeldaCruce({cruce, requisito}: {cruce: CruceRejilla; requisito: string}) {
  const {t} = useTranslation()
  if (cruce.color === null || cruce.estado === null) {
    return (
      <CeldaEstado
        nombre={
          <span className="text-xs font-normal text-muted-foreground">{t('cumplimiento.rejilla.noVerificado')}</span>
        }
        porque={requisito}
      />
    )
  }
  const corto = t(`cumplimiento.estadoCorto.${cruce.estado}`)
  return (
    <CeldaEstado
      chip={
        <Tag tone={TONOS[cruce.color as Color]} className="w-fit">
          {corto}
        </Tag>
      }
      nombre={null}
      porque={t('cumplimiento.rejilla.celdaDetalle', {requisito, estado: corto})}
    />
  )
}

/** El veredicto es un color, no un estado: con `CeldaCruce` se interpolaba «null» en la clave de copy. */
function CeldaVeredicto({fila}: {fila: FilaRejilla}) {
  const {t} = useTranslation()
  return (
    <span data-testid={`rejilla-veredicto-${fila.cedula}`}>
      <CeldaEstado
        chip={
          fila.color ? (
            <Tag tone={TONOS[fila.color as Color]} className="w-fit">
              {t(`cumplimiento.color.${fila.color}`)}
            </Tag>
          ) : undefined
        }
        nombre={
          fila.color ? null : (
            <span className="text-xs font-normal text-muted-foreground">{t('cumplimiento.rejilla.noVerificado')}</span>
          )
        }
      />
    </span>
  )
}

/** El N del pedido en bloque es el `faltan` del servidor, el mismo de la escritura; restarlo acá prometía de más. */
function EncabezadoRequisito({
  columna,
  sinSugerido,
  enLaPagina,
  puedePedir,
  onPedir,
}: {
  columna: ColumnaRejilla
  sinSugerido: boolean
  enLaPagina: {n: number; total: number}
  puedePedir: boolean
  onPedir: () => void
}) {
  const {t} = useTranslation()
  const n = sinSugerido ? columna.numeros.confirmado : columna.numeros.con_sugerido
  const enElCargo = t('cumplimiento.rejilla.conteoCargo', {n, total: columna.numeros.total})
  const enEstaPagina = t('cumplimiento.rejilla.conteoPagina', enLaPagina)
  const hayMenu = puedePedir && columna.faltan > 0
  const clave = claveDeRotulo(columna)
  return (
    // Dos renglones: en un `flex-wrap` el chip se colaba entre las palabras del rótulo.
    <span className="flex w-full flex-col items-start gap-1">
      {/* `[overflow-wrap:normal]` pisa el `break-words` que `DataTable` pone a todo `th`, que partía
          el rótulo a mitad de palabra. */}
      <span className="line-clamp-2 hyphens-none [overflow-wrap:normal] [word-break:normal]" title={columna.nombre}>
        {clave ? t(`cumplimiento.rejilla.rotulo.${clave}`) : nombreCortoDeRequisito(columna.nombre)}
      </span>
      <span className="flex items-center gap-1">
        <Tag
          tone="neutral"
          className="w-fit"
          title={`${enElCargo} · ${enEstaPagina}`}
          data-testid={`rejilla-conteo-${columna.nombre}`}>
          {t('cumplimiento.rejilla.conteoChip', {n, total: columna.numeros.total})}
          <span className="sr-only"> {enElCargo}</span>
          <span className="sr-only" data-testid={`rejilla-pagina-${columna.nombre}`}>
            {' '}
            {enEstaPagina}
          </span>
        </Tag>
        {hayMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label={t('cumplimiento.rejilla.columnaMenu', {requisito: columna.nombre})}
                data-testid={`rejilla-columna-${columna.nombre}`}>
                <KebabGlyph />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel className="max-w-64 text-xs normal-case">{columna.nombre}</DropdownMenuLabel>
              <DropdownMenuItem data-testid={`rejilla-pedir-${columna.nombre}`} onSelect={onPedir}>
                {t('cumplimiento.rejilla.pedirAFaltantes', {n: columna.faltan})}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </span>
    </span>
  )
}

function enLaPagina(filas: FilaRejilla[], requisitoId: string, sinSugerido: boolean) {
  const cruces = filas.map((fila) => fila.cruces.find((cruce) => cruce.requisito_id === requisitoId))
  const contadas = cruces.filter((cruce) => cruce?.cuenta)
  const verdes = contadas.filter((cruce) => cruce?.color === 'verde').length
  const amarillos = contadas.filter((cruce) => cruce?.estado === 'sugerido').length
  return {n: sinSugerido ? verdes : verdes + amarillos, total: contadas.length}
}

/** Orden y grupos salen juntos: si no coinciden, `startKey` apunta a una columna que no está ahí. */
function agruparPorBloque(columnas: ColumnaRejilla[], rotulo: (bloque: string) => string) {
  const bloques: string[] = []
  for (const columna of columnas) if (!bloques.includes(columna.bloque)) bloques.push(columna.bloque)
  const ordenadas = bloques.flatMap((bloque) => columnas.filter((columna) => columna.bloque === bloque))
  const grupos: ColumnGroup[] = bloques.map((bloque) => {
    const delBloque = columnas.filter((columna) => columna.bloque === bloque)
    return {
      startKey: delBloque[0].requisito_id,
      span: delBloque.length,
      // Un grupo de una sola columna mide 7rem: sin partir, el rótulo se derrama sobre el vecino.
      content: (
        <span className="block font-mono text-[10px] tracking-wider break-words uppercase">{rotulo(bloque)}</span>
      ),
    }
  })
  return {ordenadas, grupos}
}

/** Dos consultas: `Page` no tiene dónde llevar columnas dinámicas ni los conteos del cargo entero (E10-H11). */
export function RejillaCargoPage() {
  const {t} = useTranslation()
  const {businessId, positionId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const godReadOnly = useGodModeReadOnly()
  const enTelefono = useIsMobile()
  const table = useTableState('persona')
  const [sinSugerido, setSinSugerido] = useState(false)
  const [veredicto, setVeredicto] = useState<VeredictoFiltro>('todos')
  const [pidiendo, setPidiendo] = useState<ColumnaRejilla | null>(null)
  const [acuse, setAcuse] = useState<{pedidos: number; plazo: string} | null>(null)
  const columnas = useRejillaColumnas(business ? businessId : undefined, positionId)
  const filas = useRejilla(business ? businessId : undefined, positionId, {
    page: table.page,
    search: table.search,
    color: veredicto === 'todos' ? undefined : veredicto,
  })
  const pedir = usePedirEnBloque(businessId!, positionId!)
  const [error, setError] = useState<string | null>(null)

  if (guard) return guard
  if (columnas.isError) {
    return (
      <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="rejilla-error-carga">
        {translateApiError(columnas.error)}
      </p>
    )
  }
  if (!columnas.data) return <Skeleton className="h-40 w-full" />

  const cabecera = columnas.data
  const visibles = filas.data?.items ?? []
  const vacio = cabecera.titulares === 0
  const porColor = new Map(cabecera.veredictos.map((v) => [v.color ?? 'sin_veredicto', v.personas]))
  // `conteo: null` esconde el veredicto en cero: un filtro que siempre vuelve vacío no se ofrece.
  const opciones: OpcionEstado<VeredictoFiltro>[] = [
    {valor: 'todos', etiqueta: t('cumplimiento.rejilla.filtroTodos'), conteo: cabecera.titulares},
    ...VEREDICTOS.map((valor) => ({
      valor: valor as VeredictoFiltro,
      etiqueta:
        valor === 'sin_veredicto' ? t('cumplimiento.rejilla.filtroSinVeredicto') : t(`cumplimiento.color.${valor}`),
      conteo: porColor.get(valor) || null,
    })),
  ]

  const {ordenadas, grupos} = agruparPorBloque(cabecera.columnas, (bloque) => t(`cumplimiento.bloques.${bloque}`))
  const anchoRequisito = anchoDeColumna(
    ordenadas.map((columna) => {
      const clave = claveDeRotulo(columna)
      return clave ? t(`cumplimiento.rejilla.rotulo.${clave}`) : nombreCortoDeRequisito(columna.nombre)
    })
  )
  const columnasDeRequisito: Column<FilaRejilla>[] = ordenadas.map((columna) => ({
    key: columna.requisito_id,
    header: (
      <EncabezadoRequisito
        columna={columna}
        sinSugerido={sinSugerido}
        enLaPagina={enLaPagina(visibles, columna.requisito_id, sinSugerido)}
        puedePedir={!godReadOnly}
        onPedir={() => setPidiendo(columna)}
      />
    ),
    width: 'w-28',
    cell: (fila) => {
      const cruce = fila.cruces.find((c) => c.requisito_id === columna.requisito_id)
      return cruce ? <CeldaCruce cruce={cruce} requisito={columna.nombre} /> : null
    },
  }))
  const columns: Column<FilaRejilla>[] = [
    {
      key: 'persona',
      header: t('cumplimiento.rejilla.persona'),
      width: 'w-56',
      sticky: enTelefono ? undefined : 'left-0',
      cell: (fila) => (
        <Link className="underline" to={`/businesses/${businessId}/empleados/${fila.employee_id}?tab=cumplimiento`}>
          {fila.nombre}
        </Link>
      ),
    },
    {
      key: 'veredicto',
      header: t('cumplimiento.rejilla.veredicto'),
      headerAside: (
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('cumplimiento.rejilla.veredicto')})}
          testId="rejilla-ayuda-veredicto">
          {t('cumplimiento.rejilla.veredictoAyuda')}
        </HelpIcon>
      ),
      width: 'w-32',
      sticky: enTelefono ? undefined : 'left-56',
      cell: (fila) => <CeldaVeredicto fila={fila} />,
    },
    ...(enTelefono ? [] : columnasDeRequisito),
  ]

  return (
    <div className="space-y-4">
      <PageHeading title={t('cumplimiento.rejilla.titulo', {cargo: cabecera.cargo})} />
      {vacio ? (
        <p className="text-sm text-muted-foreground" data-testid="rejilla-vacio">
          {t('cumplimiento.rejilla.vacio', {cargo: cabecera.cargo})}{' '}
          <Link className="underline" to={`/businesses/${businessId}/organigrama`}>
            {t('cumplimiento.rejilla.vacioAccion')}
          </Link>
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3" data-testid="rejilla-numeros">
            <FiltrosDeEstado
              opciones={opciones}
              valor={veredicto}
              onValor={(valor) => {
                setVeredicto(valor)
                table.setPage(1)
              }}
              testIdPrefijo="rejilla-veredicto-filtro"
            />
            <Label className="flex items-center gap-2 text-xs">
              <Switch
                checked={sinSugerido}
                onCheckedChange={setSinSugerido}
                data-testid="rejilla-sin-sugerido"
                aria-label={t('cumplimiento.numeros.interruptor')}
              />
              {t('cumplimiento.numeros.interruptor')}
            </Label>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={table.searchInput}
              onChange={table.setSearchInput}
              placeholder={t('cumplimiento.rejilla.persona')}
              data-testid="rejilla-buscar"
            />
          </div>
          {acuse && (
            <p className="notice-amber rounded-md px-3 py-2 text-sm" data-testid="rejilla-acuse">
              {t('cumplimiento.rejilla.pedidos', {count: acuse.pedidos, plazo: formatDateOnly(acuse.plazo)})}
            </p>
          )}
          {filas.isError && (
            <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="rejilla-error-filas">
              {translateApiError(filas.error)}
            </p>
          )}
          {cabecera.columnas.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="rejilla-sin-columnas">
              {t('cumplimiento.rejilla.sinColumnas')}
            </p>
          ) : (
            <div data-testid="rejilla">
              <DataTable
                columns={columns}
                rows={visibles}
                rowKey={(fila) => fila.employee_id}
                rowTestId={(fila) => `rejilla-fila-${fila.cedula}`}
                columnGroups={enTelefono ? undefined : grupos}
                // `w-56` + `w-32` + los requisitos: el scroll horizontal queda dentro de la tabla en vez de repartir el ancho.
                minWidth={enTelefono ? undefined : `${14 + 8 + ordenadas.length * anchoRequisito}rem`}
              />
            </div>
          )}
          {filas.data && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <PaginationBar
                page={filas.data.page}
                pageSize={filas.data.page_size}
                total={filas.data.total}
                onPageChange={table.setPage}
              />
              <span className="text-xs text-muted-foreground" data-testid="rejilla-titulares">
                {t('cumplimiento.rejilla.titulares', {count: cabecera.titulares})}
              </span>
            </div>
          )}
        </>
      )}
      <Link className="text-sm underline" to={`/businesses/${businessId}/sgsst/perfiles-cargo/${positionId}`}>
        {t('cumplimiento.rejilla.volver')}
      </Link>
      <PedirSoporteDialog
        key={`rejilla-pedir-${pidiendo?.requisito_id ?? 'ninguna'}`}
        abierto={!!pidiendo}
        titulo={t('cumplimiento.rejilla.pedirAFaltantes', {n: pidiendo?.faltan ?? 0})}
        notaPorDefecto={pidiendo?.nombre ?? ''}
        testId="rejilla"
        onCerrar={() => setPidiendo(null)}
        guardando={pedir.isPending}
        error={error}
        onConfirmar={(plazo, nota) => {
          setError(null)
          pedir.mutate(
            {requisitoId: pidiendo!.requisito_id, plazo, nota},
            {
              onSuccess: (data) => {
                setPidiendo(null)
                setAcuse({pedidos: data.pedidos, plazo: data.plazo})
              },
              onError: (err) => setError(translateApiError(err)),
            }
          )
        }}
      />
    </div>
  )
}
